import { useMemo } from 'react';
import { IChainEndDeclineRatio } from '@nkr/models/index';
import { AgGridReact } from 'ag-grid-react';
import { IHeaderParams, GridReadyEvent, ICellRendererParams, ValueGetterParams, FirstDataRenderedEvent, RowClassParams, ColSpanParams, ColDef, RowDataChangedEvent } from 'ag-grid-community';
import { ReportWrapper } from './styles';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { Utility } from '@nkr/services/utility';

interface IProps {
    data: IChainEndDeclineRatio[],
    totals: IChainEndDeclineRatio,
    onGridReady: (e: GridReadyEvent) => void
}

const Grid = (props: IProps) => {
    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const getRowStyle = (params: RowClassParams) => {
        let data = params.data as IChainEndDeclineRatio;

        if (params.node.rowPinned) {
            return { 'background-color': '#133366;', color: 'white' };
        }

        if (data.chainStarts == 0) {
            return { backgroundColor: 'lightblue' };
        }
    };

    const cols: ColDef[] = [
        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerName: 'Center',
            width: 155,
            comparator: Utility.stringSortComparator,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Center</SortableHeaderComponent>,
            pinnedRowCellRendererFramework: (props: ICellRendererParams) => {
                return <div className='text-right'>Summary &gt;&gt;</div>;
            },
            colSpan: (params: ColSpanParams) => {
                if (params.node.rowPinned) {
                    return 2;
                }

                return 1;
            }
        },
        {
            colId: 'isParticipant',
            field: 'isParticipant',
            headerName: 'Member Center',
            width: 125,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Member Center</SortableHeaderComponent>,
            valueGetter: (props: ValueGetterParams) => props.data.isParticipant ? 'Yes' : 'No'
        },
        {
            colId: 'chipDeclines',
            field: 'chipDeclines',
            headerName: 'Declines',
            width: 125,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Declines</SortableHeaderComponent>,
        },
        {
            colId: 'adpStarts',
            field: 'adpStarts',
            headerName: 'Voucher Chain Starts',
            width: 205,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Voucher Chain Starts</SortableHeaderComponent>,
        },
        {
            colId: 'nddStarts',
            field: 'nddStarts',
            headerName: 'NDD Chain Starts',
            width: 155,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>NDD Chain Starts</SortableHeaderComponent>,
        },
        {
            colId: 'chainStarts',
            field: 'chainStarts',
            headerName: 'Total Chain Starts',
            width: 155,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Total Chain Starts</SortableHeaderComponent>,
        },
        {
            colId: 'wlpOffers',
            field: 'wlpOffers',
            headerName: 'Total WLP Offers',
            width: 155,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Total WLP Offers</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IChainEndDeclineRatio;
                return data.wlpOffers < 0 ? <>NV</> : <>{data.wlpOffers}</>
            }
        },
        {
            colId: 'wlpDeclines',
            field: 'wlpDeclines',
            headerName: 'Total WLP Declines',
            width: 155,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Total WLP Declines</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IChainEndDeclineRatio;
                return data.wlpDeclines < 0 ? <>NV</> : <>{data.wlpDeclines}</>
            }
        },
        {
            colId: 'declineRatio',
            field: 'declineRatio',
            headerName: 'Decline Ratio',
            width: 155,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Decline Ratio</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IChainEndDeclineRatio;
                return data.declineRatio < 0 ? <>NV</> : <>{data.declineRatio}</>
            }
        },
        {
            colId: 'rank',
            field: 'rank',
            headerName: 'Rank',
            width: 75,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
            pinnedRowCellRendererFramework: (props: ICellRendererParams) => {
                return <div></div>;
            },
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IChainEndDeclineRatio;
                return data.rank == -1 ? <>NV</> : <>{props.value}</>
            }
        }
    ];

    const columnDefs = useMemo(() => cols, []);
    const rowData = useMemo(() => props.data, [props.data]);
    const totalsData = useMemo(() => props.totals, [props.totals]);

    return (
        <ReportWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: false,
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={28}
                rowHeight={30}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                suppressContextMenu={true}
                noRowsOverlayComponentFramework={() => <div>No center information available...</div>}
                pinnedBottomRowData={[totalsData]}
                suppressHorizontalScroll={true}
                onGridReady={props.onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onRowDataChanged={onRowDataChanged}
                getRowStyle={getRowStyle}
            />
        </ReportWrapper>
    )
}

export default Grid;